// Login screen — «вход по ключу из почты» is the primary flow: email →
// one-time 6-digit code from the mailbox → logged in (boards card cmw6815).
// Password login stays available as a secondary view for existing users and
// admins («Войти по паролю»), alongside the Yandex entry point — humanteq
// only: the breakup funnel is fully passwordless (#4487), so its build never
// offers the password view.

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useYandexAuth } from '../../hooks/useYandexAuth'
import { useCountdown } from '../../hooks/useCountdown'
import { authApi } from '../../api/auth'
import { ApiError, messageFromError } from '../../api/client'
import { usePendingSegment } from '../../features/product-segment'
import CodeInput from '../../components/CodeInput'
import PasswordInput from '../../components/PasswordInput'
import YandexButton from '../../components/YandexButton'
import s from '../auth.module.css'

type Mode = 'code' | 'password'

export default function Login() {
  const { login, requestLoginCode, loginByCode } = useAuth()
  const yandex = useYandexAuth()
  // #4487: the breakup audience is new and passwordless — no password entry
  // point on its build. humanteq keeps the secondary password view.
  const allowPassword = usePendingSegment((st) => st.segment) !== 'break_up'

  const [mode, setMode] = useState<Mode>('code')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Code-flow state: once a code is dispatched, swap the email form for the
  // CodeInput with a 60s resend cooldown.
  const [codeSentTo, setCodeSentTo] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState('')
  const [verifying, setVerifying] = useState(false)
  const codeResend = useCountdown()

  // Password-flow state (secondary view).
  const [password, setPassword] = useState('')
  // When the backend reports email_not_verified we offer a resend link with a
  // 60s cooldown (original looped a countdown and restored the link, auth.js:856).
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null)
  const [resendNote, setResendNote] = useState('')
  const verifyResend = useCountdown()

  const requestCode = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!email.trim()) return setError('Введите email')
    setError('')
    setLoading(true)
    try {
      await requestLoginCode({ email: email.trim() })
      setCodeSentTo(email.trim())
      setCode('')
      setCodeError('')
      codeResend.start(60)
    } catch (err) {
      setError(messageFromError(err, 'Не удалось отправить код. Попробуйте позже.'))
      // 429 cooldown — hold the button for the window instead of letting
      // the user hammer it into repeated 429s (QA 2026-10-27).
      if (err instanceof ApiError && err.status === 429) codeResend.start(60)
    } finally {
      setLoading(false)
    }
  }

  const submitCode = async (value?: string) => {
    const finalCode = value || code
    if (finalCode.length !== 6 || !codeSentTo) return
    setVerifying(true)
    setCodeError('')
    try {
      await loginByCode({ email: codeSentTo, code: finalCode })
    } catch (err) {
      setCodeError(messageFromError(err, 'Неверный код'))
      setCode('')
    } finally {
      setVerifying(false)
    }
  }

  const resendCode = async () => {
    if (codeResend.active || !codeSentTo) return
    try {
      await authApi.requestLoginCode({ email: codeSentTo })
    } catch (err) {
      // Show why the resend didn't happen (cooldown seconds / dispatch
      // failure) — a silent catch reads as "nothing works" on the login screen.
      setCodeError(messageFromError(err, 'Не удалось отправить код. Попробуйте позже.'))
      if (err instanceof ApiError && err.status === 429) codeResend.start(60)
      return
    }
    codeResend.start(60)
  }

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setUnverifiedEmail(null)
    try {
      await login({ username: email, password })
    } catch (err) {
      const detail = err instanceof ApiError ? err.body?.detail : undefined
      // Narrow the 402/403-style object form (string and 422-array forms fall through).
      const obj = detail && typeof detail === 'object' && !Array.isArray(detail) ? detail : undefined
      if (err instanceof ApiError && err.status === 403 && obj?.reason === 'email_not_verified') {
        setUnverifiedEmail((obj as { email?: string }).email || email)
      } else {
        setError(typeof detail === 'string' ? detail : obj?.message || 'Неверный логин или пароль')
      }
    } finally {
      setLoading(false)
    }
  }

  const resendVerification = async () => {
    if (verifyResend.active || !unverifiedEmail) return
    setResendNote('Отправка...')
    try {
      await authApi.resendVerification({ email: unverifiedEmail })
      setResendNote('Письмо отправлено. Проверьте почту.')
      verifyResend.start(60)
    } catch {
      setResendNote('Ошибка. Попробуйте позже.')
    }
  }

  const oauthError = yandex.error
  const busy = loading || yandex.loading

  return (
    <div className={s.screen}>
      <div className={s.content}>
        <div className={s.appLogo}>
          <img src="/assets/app-icon.png" alt="Humanteq" />
        </div>
        <div className={s.header}>
          <h1 className={s.title}>Вход</h1>
          <p className={s.subtitle}>
            {mode === 'code'
              ? codeSentTo
                ? 'Код отправлен на вашу почту'
                : 'Введите email — пришлём одноразовый код для входа'
              : 'Введите данные для входа'}
          </p>
        </div>

        {mode === 'code' && !codeSentTo && (
          <form className={s.form} onSubmit={requestCode}>
            <div className={s.fieldGroup}>
              <input
                className={s.input}
                type="email"
                placeholder="Email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <button
              className={s.button}
              type="submit"
              disabled={busy || codeResend.active}
            >
              {loading
                ? 'Отправка...'
                : codeResend.active
                  ? `Подождите ${codeResend.remaining} сек.`
                  : 'Получить код'}
            </button>
            {(error || oauthError) && (
              <p className={s.error} style={{ marginTop: 10 }}>{error || oauthError}</p>
            )}
          </form>
        )}

        {mode === 'code' && codeSentTo && (
          <>
            <p className={s.muted} style={{ marginBottom: 16 }}>
              Код отправлен на <strong>{codeSentTo}</strong>.{' '}
              <button
                type="button"
                className={s.link}
                onClick={() => {
                  setCodeSentTo(null)
                  setCode('')
                  setCodeError('')
                }}
              >
                Изменить email
              </button>
            </p>

            <CodeInput value={code} onChange={setCode} onComplete={submitCode} disabled={verifying} />

            <button
              className={s.button}
              style={{ marginTop: 16 }}
              onClick={() => submitCode()}
              disabled={verifying || code.length !== 6}
            >
              {verifying ? 'Проверка...' : 'Войти'}
            </button>

            {codeError && <p className={s.error} style={{ marginTop: 12 }}>{codeError}</p>}

            <p className={s.muted}>
              {codeResend.active ? (
                `Повторная отправка через ${codeResend.remaining} сек.`
              ) : (
                <button type="button" className={s.link} onClick={resendCode}>
                  Отправить код повторно
                </button>
              )}
            </p>
          </>
        )}

        {mode === 'password' && (
          <form className={s.form} onSubmit={submitPassword}>
            <div className={s.fieldGroup}>
              <input
                className={s.input}
                type="text"
                placeholder="Email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Пароль"
                autoComplete="current-password"
              />
            </div>
            <button className={s.button} type="submit" disabled={busy}>
              {loading ? 'Вход...' : 'Войти'}
            </button>

            {unverifiedEmail && (
              <p className={s.error} style={{ marginTop: 10 }}>
                Email не подтверждён.{' '}
                {resendNote && <span>{resendNote} </span>}
                {verifyResend.active ? (
                  <span>Повторная отправка через {verifyResend.remaining} сек.</span>
                ) : (
                  <button type="button" className={s.link} onClick={resendVerification}>
                    Отправить письмо повторно
                  </button>
                )}
              </p>
            )}

            {(error || oauthError) && !unverifiedEmail && (
              <p className={s.error} style={{ marginTop: 10 }}>{error || oauthError}</p>
            )}
          </form>
        )}

        {mode === 'password' && (
          <p className={s.muted}>
            <Link className={s.link} to="/forgot-password">Забыли пароль?</Link>
          </p>
        )}

        <div className={s.divider}>или</div>

        <YandexButton label="Войти через Яндекс" onClick={yandex.signIn} disabled={busy} />

        {allowPassword && (
          <p className={s.muted} style={{ marginTop: 16 }}>
            <button
              type="button"
              className={s.link}
              onClick={() => {
                setMode(mode === 'code' ? 'password' : 'code')
                setError('')
                setCodeSentTo(null)
              }}
            >
              {mode === 'code' ? 'Войти по паролю' : 'Войти по коду из письма'}
            </button>
          </p>
        )}

        <div className={s.authBottom}>
          Нет аккаунта? <Link to="/register">Зарегистрироваться</Link>
        </div>
      </div>
    </div>
  )
}
